import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Modal, Form, Input, Select, Button, message, Divider, Row, Col, Card, Alert, Tag } from 'antd';
import { generateAIToolsMaterialPresignedUrl } from '../../api/s3';
import { getGoogleToken, getDriveFiles, getCalendarEvents } from '../../api/ctrag';
import { meetingSummarization } from '../../api/aiTools';
import './meetingSummaryForm.scss';
import SingleUpload from '../../components/SingleUpload';
import MultiUpload from '../../components/MultiUpload';
import LanguageSelector from '../../components/LanguageSelector';
import MeetingCalendar from '../../components/MeetingCalendar/MeetingCalendar';
import EventDetailsDisplay from '../../components/MeetingCalendar/EventDetailsDisplay';
import SettingContext from '../../contexts/settingProvider';
import { extractGoogleDriveId, parseRecurringEventId } from '../../utils/functionTool';
import { useTranslation } from 'react-i18next';
import { 
  openDrivePicker,
  MIME_TYPE_GROUPS,
  DEFAULT_VIEW_ID
} from '../../utils/googlePickerConfig';
import {
  getDocumentAcceptTypes,
  getAudioVideoAcceptTypes,
  isDocumentFile,
  isAudioVideoFile
} from '../../constants/fileTypes';
import { IS_STAGING } from '../../utils/config'; // Staging 環境特殊處理

import axios from 'axios';
import dayjs from 'dayjs';

const { TextArea } = Input;
const MAX_ATTACHMENT_FILES = 10; // Define max attachments

/**
 * 處理 Google Drive 檔案的結果，統一處理權限問題
 * @param {Object} fileDetail - getDriveFiles API 返回的檔案詳細資訊
 * @param {Object} baseFileInfo - 基本檔案資訊 (來自 Picker 或 Calendar)
 * @param {Function} setFilePermissionIssues - 更新權限問題檔案列表的函數
 * @param {Function} setHasAnyFilePermissionIssue - 設置是否有權限問題的函數
 * @param {Function} setShowPermissionAlert - 設置是否顯示權限警告的函數
 * @param {Function} t - 翻譯函數
 * @returns {Object} 處理後的檔案物件，包含標準化的屬性
 */
function processDriveFileResult(fileDetail, baseFileInfo, setFilePermissionIssues, setHasAnyFilePermissionIssue, setShowPermissionAlert, t) {
  // 處理 API 返回錯誤的情況 (status: 2)
  if (fileDetail && fileDetail.status === 2) {
    const fileId = fileDetail.source_info?.file_id || baseFileInfo.id;
    
    if (!fileId) {
      console.warn('檔案請求失敗但無法獲取檔案ID:', fileDetail);
      return null;
    }
    
    const failedFile = {
      uid: fileId,
      id: fileId,
      name: baseFileInfo.name || baseFileInfo.source_name || t('aiAssistant.meetingSummary.form.messages.unnamedFile', { fileId: fileId.substring(0, 8) }),
      status: 'done', // Keep status 'done' for AntD list, hasPermission will gatekeep
      url: baseFileInfo.url || baseFileInfo.source_url || '',
      source_name: baseFileInfo.name || baseFileInfo.source_name || t('aiAssistant.meetingSummary.form.messages.unnamedFile', { fileId: fileId.substring(0, 8) }),
      source_type: baseFileInfo.mimeType || baseFileInfo.source_type || '',
      source_source: 'drive',
      source_url: baseFileInfo.url || baseFileInfo.source_url || '',
      source_id: fileId,
      hasPermission: false,
      permissionError: fileDetail.message || t('aiAssistant.meetingSummary.form.messages.fileInfoFailed')
    };
    
    // 更新權限問題檔案列表
    setFilePermissionIssues(prev => {
      const exists = prev.some(f => f.id === fileId);
      if (!exists) return [...prev, {
        id: fileId, 
        name: failedFile.name, 
        permissionError: failedFile.permissionError,
        url: failedFile.url || failedFile.source_url
      }];
      return prev;
    });
    
    // 設置權限問題標誌和顯示警告
    setHasAnyFilePermissionIssue(true);
    setShowPermissionAlert(true);
    
    // 返回處理後的檔案物件
    return failedFile;
  }
  
  // 處理 API 成功但檔案沒有下載權限的情況
  if (fileDetail && fileDetail.file_info) {
    const detailedFile = fileDetail.file_info;
    const canAccess = detailedFile.capabilities?.canDownload;
    
    const processedFile = {
      uid: detailedFile.id,
      id: detailedFile.id,
      name: detailedFile.name,
      status: 'done',
      url: detailedFile.webViewLink,
      source_name: detailedFile.name,
      source_type: detailedFile.mimeType,
      source_source: 'drive',
      source_url: detailedFile.webViewLink,
      source_id: detailedFile.id,
      size: detailedFile.size,
      createdTime: detailedFile.createdTime,
      hasPermission: canAccess === true,
      permissionError: canAccess === false ? t('aiAssistant.meetingSummary.form.messages.downloadPermissionDenied') : undefined
    };
    
    // 處理權限不足的情況
    if (canAccess === false) {
      setFilePermissionIssues(prev => {
        const exists = prev.some(f => f.id === processedFile.id);
        if (!exists) return [...prev, {
          id: processedFile.id, 
          name: processedFile.name, 
          permissionError: processedFile.permissionError,
          url: processedFile.url || processedFile.source_url
        }];
        return prev;
      });
      
      setHasAnyFilePermissionIssue(true);
      setShowPermissionAlert(true);
    }
    
    return processedFile;
  }
  
  // 如果沒有檔案資訊，返回 null
  console.warn('從 getDriveFiles 收到的檔案資訊不完整', fileDetail);
  return null;
}

/**
 * 搜索週期性會議事件並載入最新的會議記錄附件
 * @param {object} currentEvent - 當前選擇的事件
 * @param {string} googleToken - Google 存取權杖
 * @param {Function} setFilePermissionIssues - 設置檔案權限問題的函數
 * @param {Function} setHasAnyFilePermissionIssue - 設置是否有檔案權限問題的函數
 * @param {Function} setShowPermissionAlert - 設置是否顯示權限警告的函數
 * @param {Function} t - 翻譯函數
 * @returns {Array} 找到的會議記錄附件陣列
 */
async function searchAndLoadRecurringMeetingNotes(currentEvent, googleToken, setFilePermissionIssues, setHasAnyFilePermissionIssue, setShowPermissionAlert, t) {
  if (!currentEvent?.id || !googleToken) {
    return [];
  }

  try {
    const baseEventId = parseRecurringEventId(currentEvent.id);
    
    // 如果解析後的 ID 和原 ID 相同，表示不是週期性事件
    if (baseEventId === currentEvent.id) {
      console.log('非週期性事件，跳過會議記錄搜索');
      return [];
    }

    // 計算搜索範圍：往回 3 個月
    const threeMonthsAgo = dayjs().subtract(3, 'month').startOf('day');
    const currentEventDate = dayjs(currentEvent.start?.dateTime || currentEvent.start?.date || currentEvent.start);
    
    console.log(`搜索週期性會議記錄：基礎ID=${baseEventId}，範圍=${threeMonthsAgo.format('YYYY-MM-DD')} 到 ${currentEventDate.format('YYYY-MM-DD')}`);

    // 使用 getCalendarEvents API 搜索相關事件
    // 由於 API 不支援 q 參數搜索，我們需要獲取時間範圍內的所有事件並過濾
    const events = await getCalendarEvents({
      start: threeMonthsAgo.toISOString(),
      end: currentEventDate.toISOString(),
      fields: 'extended'
    });

    if (!events || !Array.isArray(events)) {
      console.log('未找到相關事件');
      return [];
    }

    // 過濾出同一系列的週期性事件（排除當前選擇的事件）
    const relatedEvents = events.filter(event => {
      if (!event.id || event.id === currentEvent.id) return false;
      const eventBaseId = parseRecurringEventId(event.id);
      return eventBaseId === baseEventId;
    });

    if (relatedEvents.length === 0) {
      console.log('未找到相關的週期性事件');
      return [];
    }

    // 按時間排序，找到最新的一個事件
    const sortedEvents = relatedEvents.sort((a, b) => {
      const aTime = dayjs(a.start?.dateTime || a.start?.date || a.start);
      const bTime = dayjs(b.start?.dateTime || b.start?.date || b.start);
      return bTime.diff(aTime); // 降序排列，最新的在前
    });

    const latestEvent = sortedEvents[0];
    console.log(`找到最新的相關事件：${latestEvent.title || latestEvent.summary}，時間：${dayjs(latestEvent.start?.dateTime || latestEvent.start?.date || latestEvent.start).format('YYYY-MM-DD HH:mm')}`);

    // 檢查最新事件是否有附件
    if (!latestEvent.attachments || latestEvent.attachments.length === 0) {
      console.log('最新的相關事件沒有附件');
      return [];
    }

    // 篩選出以 "[會議記錄]" 開頭的附件
    const meetingNoteAttachments = latestEvent.attachments.filter(attachment => {
      const title = attachment.title || '';
      return title.startsWith('[會議記錄]');
    });

    if (meetingNoteAttachments.length === 0) {
      console.log('最新的相關事件沒有會議記錄附件');
      return [];
    }

    console.log(`找到 ${meetingNoteAttachments.length} 個會議記錄附件`);

    // 處理會議記錄附件，類似於處理普通附件的邏輯
    const meetingNoteFileIds = [];
    for (const attachment of meetingNoteAttachments) {
      const fileId = extractGoogleDriveId(attachment.url || attachment.fileUrl);
      if (fileId) {
        meetingNoteFileIds.push(fileId);
      }
    }

    if (meetingNoteFileIds.length === 0) {
      console.log('無法從會議記錄附件中提取 Google Drive 文件 ID');
      return [];
    }

    // 使用 getDriveFiles API 獲取文件詳細資訊
    const fileDetailsArray = await getDriveFiles(meetingNoteFileIds, false);
    const processedMeetingNotes = [];

    for (const fileDetail of fileDetailsArray) {
      // 找到對應的原始附件資訊
      let originalAttachment = null;
      if (fileDetail && (fileDetail.status === 2 || fileDetail.file_info)) {
        const fileId = fileDetail.status === 2 
          ? fileDetail.source_info?.file_id 
          : fileDetail.file_info?.id;
        
        if (fileId) {
          originalAttachment = meetingNoteAttachments.find(a => {
            const attachmentFileId = extractGoogleDriveId(a.url || a.fileUrl);
            return attachmentFileId === fileId;
          });
        }
      }
      
      const processedFile = processDriveFileResult(
        fileDetail, 
        originalAttachment || { id: fileDetail.requested_id }, 
        setFilePermissionIssues, 
        setHasAnyFilePermissionIssue, 
        setShowPermissionAlert,
        t
      );
      
      if (processedFile) {
        // 為前次會議的附件添加特殊標記
        processedFile.isFromPreviousMeeting = true;
        processedFile.previousMeetingDate = dayjs(latestEvent.start?.dateTime || latestEvent.start?.date || latestEvent.start).format('YYYY/MM/DD');
        processedMeetingNotes.push(processedFile);
      }
    }

    if (processedMeetingNotes.length > 0) {
      message.success(t('aiAssistant.meetingSummary.form.messages.recurringMeetingNotesLoaded', { 
        count: processedMeetingNotes.length,
        meetingDate: dayjs(latestEvent.start?.dateTime || latestEvent.start?.date || latestEvent.start).format('YYYY/MM/DD')
      }));
    }

    return processedMeetingNotes;

  } catch (error) {
    console.error('搜索週期性會議記錄時發生錯誤:', error);
    message.warning(t('aiAssistant.meetingSummary.form.messages.recurringMeetingNotesLoadFailed', { 
      error: error.message || '未知錯誤' 
    }));
    return [];
  }
}

const MeetingSummaryForm = ({ visible, onCancel, onAfterSubmit, user }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedLanguage, setSelectedLanguage] = useState('zh-TW');
  const [audioFile, setAudioFile] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [isAudioUploading, setIsAudioUploading] = useState(false);
  const [areAttachmentsUploading, setAreAttachmentsUploading] = useState(false);
  const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false);
  const [selectedEventDetails, setSelectedEventDetails] = useState(null);
  const [googleAccessTokenForCalendar, setGoogleAccessTokenForCalendar] = useState(null);
  const { dark } = useContext(SettingContext);

  // Staging 環境特殊處理: 直接跳到編輯畫面
  const [formStep, setFormStep] = useState(IS_STAGING ? 'formVisible' : 'initialChoice'); // 'initialChoice', 'formVisible'
  const [meetingSource, setMeetingSource] = useState(IS_STAGING ? 'manual' : null); // null, 'calendar', 'manual'
  const [hasAnyFilePermissionIssue, setHasAnyFilePermissionIssue] = useState(false);
  const [filePermissionIssues, setFilePermissionIssues] = useState([]);
  const [showPermissionAlert, setShowPermissionAlert] = useState(true);

  const resetAllStates = useCallback(() => {
    form.resetFields();
    setAudioFile(null);
    setAttachmentFiles([]);
    setSelectedEventDetails(null);
    setGoogleAccessTokenForCalendar(null);
    setIsAudioUploading(false);
    setAreAttachmentsUploading(false);
    // Staging 環境特殊處理: 重置時直接跳到編輯畫面
    setFormStep(IS_STAGING ? 'formVisible' : 'initialChoice');
    setMeetingSource(IS_STAGING ? 'manual' : null);
    // 清除權限問題相關狀態
    setFilePermissionIssues([]);
    setHasAnyFilePermissionIssue(false);
    setShowPermissionAlert(true); // 重設為 true，以便下次有問題時能顯示
    // Note: isCalendarModalVisible is handled separately by its own open/close logic
  }, [form]);
  
  const handleOverallCancel = () => {
    resetAllStates();
    if (typeof onCancel === 'function') {
      onCancel(); // Call parent's onCancel to close the entry point
    }
  };

  useEffect(() => {
    if (visible && !IS_STAGING && formStep === 'initialChoice' && !meetingSource) {
      // Reset states if re-entering the form, ensuring a clean start for initial choice
      resetAllStates();
      setFormStep('initialChoice'); // Explicitly set to initialChoice
    } else if (visible && IS_STAGING && formStep !== 'formVisible') {
      // Staging 環境特殊處理: 跳過選擇會議步驟，直接進入編輯畫面
      resetAllStates();
      setFormStep('formVisible');
      setMeetingSource('manual');
    } else if (!visible) {
      // If the parent hides the component, reset everything
      resetAllStates();
    }
  }, [visible, resetAllStates, meetingSource, formStep]);

  // 不再需要預先載入舊版 Google Picker API

  const createPicker = async (fileType) => {
    try {
      // 根據檔案類型選擇合適的 MIME 類型
      const mimeTypes = fileType === 'audio' 
        ? MIME_TYPE_GROUPS.MEDIA_FILES
        : MIME_TYPE_GROUPS.MEETING_DOCUMENTS;

      const result = await openDrivePicker({
        mimeTypes,
        includeFolders: true,
        enableDrives: true,
        viewId: DEFAULT_VIEW_ID,
        title: fileType === 'audio' 
          ? t('aiAssistant.meetingSummary.form.selectAudioFile')
          : t('aiAssistant.meetingSummary.form.selectAttachments'),
        locale: (typeof t === 'function' && (t.i18n?.language)) ? t.i18n.language : undefined,
      });

      if (result?.docs?.length) {
        if (fileType === 'audio') {
          const file = result.docs[0]; // Audio is single
          await handleDriveFile(file, fileType);
        } else {
          await handleMultipleDriveAttachments(result.docs);
        }
      } else {
        if (fileType === 'audio') {
          setIsAudioUploading(false);
        } else {
          setAreAttachmentsUploading(false);
        }
      }
    } catch (error) {
      console.error('創建 Google Picker 失敗:', error);
      message.error(t('aiAssistant.meetingSummary.form.messages.pickerCreateFailed', { error: error.message }));
    }
  };

  // 舊版 picker callback 已移除，直接使用 openDrivePicker 回傳的 docs 處理

  const handleDriveFile = async (fileInfo, fileType) => { // fileInfo can be from picker or calendar
    const baseFile = {
      id: fileInfo.id,
      source_name: fileInfo.name || fileInfo.source_name,
      source_type: fileInfo.mimeType || fileInfo.source_type,
      source_source: 'drive',
      source_url: fileInfo.url || fileInfo.source_url, // Picker provides 'url', calendar might provide 'source_url' via our transform
      source_id: fileInfo.id,
    };

    try {
      if (fileType === 'audio') {
        setIsAudioUploading(true);
      } else {
        setAreAttachmentsUploading(true); 
      }
      const fileDetails = await getDriveFiles([baseFile.id], false);
      
      const processedFile = processDriveFileResult(fileDetails[0], baseFile, setFilePermissionIssues, setHasAnyFilePermissionIssue, setShowPermissionAlert, t);
      
      if (processedFile) {
        if (fileType === 'audio') {
          setAudioFile(processedFile);
          form.setFieldValue('media_path', processedFile);
          if(fileInfo.source_name && processedFile.hasPermission){ // only show success if it's from a picker action or calendar action with name and has permission
             message.success(t('aiAssistant.meetingSummary.form.messages.driveFileSelected', { fileName: processedFile.source_name }));
          } else if (!processedFile.hasPermission) {
            message.warning(t('aiAssistant.meetingSummary.form.messages.driveFilePermissionIssue', { fileName: processedFile.source_name }));
          }
        } else { 
          setAttachmentFiles(prevFiles => {
            const exists = prevFiles.some(f => f.id === processedFile.id);
            if (exists) return prevFiles;
            return [...prevFiles, processedFile];
          });
          form.setFieldValue('material_paths', [...(form.getFieldValue('material_paths') || []), processedFile]);
          // Message for adding new attachment is handled in handleMultipleDriveAttachments or here if single
        }
      }
    } catch (error) {
      console.error('處理 Google Drive 文件選擇失敗:', error);
      message.error(t('aiAssistant.meetingSummary.form.messages.driveFileProcessFailed', { error: error.message }));
    } finally {
      if (fileType === 'audio') {
        setIsAudioUploading(false);
      } else {
        setAreAttachmentsUploading(false);
      }
    }
  };

  const handleFileUpload = async (file, user) => {
    try {
      const presignedUrlInfo = await generateAIToolsMaterialPresignedUrl(file.name);
      const formData = new FormData();
      Object.entries(presignedUrlInfo.presigned_post_data.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);
      
      await axios.post(presignedUrlInfo.presigned_post_data.url, formData);
      
      const newFile = { 
        uid: presignedUrlInfo.source_info.s3_url, // Added for AntD Upload compatibility
        name: file.name, // Added for AntD Upload compatibility (maps to source_name)
        status: 'done', // Added for AntD Upload compatibility
        url: presignedUrlInfo.source_info.s3_url, // Added for AntD Upload compatibility (maps to source_url)
        source_name: file.name,
        source_type: file.type,
        source_source: 'local',
        source_url: presignedUrlInfo.source_info.s3_url,
        size: file.size 
      };
      return newFile;
    } catch (error) {
      throw error;
    }
  };

  const handleAudioFileChange = async (info) => {
    const file = info.file;
    if (audioFile) {
      message.warning(t('aiAssistant.meetingSummary.form.messages.singleAudioOnly'));
    }

    setIsAudioUploading(true);

    try {
      const newFile = await handleFileUpload(file, user);
      setAudioFile(newFile);
      form.setFieldValue('media_path', newFile);
      message.success(t('aiAssistant.meetingSummary.form.messages.audioUploadSuccess'));
    } catch (error) {
      console.error('音檔上傳失敗:', error);
      message.error(t('aiAssistant.meetingSummary.form.messages.audioUploadFailed'));
    } finally {
      setIsAudioUploading(false);
    }
  };

  const handleAddAttachmentFile = async (fileInfo) => { 
    setAreAttachmentsUploading(true); 

    const rawFile = fileInfo.originFileObj || fileInfo;

    // Check for duplicates before attempting upload
    const isDuplicate = attachmentFiles.find(f =>
      f.source_source === 'local' &&
      f.source_name === rawFile.name &&
      f.size === rawFile.size &&
      f.source_type === rawFile.type
    );

    if (isDuplicate) {
      message.warning(t('aiAssistant.meetingSummary.form.messages.attachmentDuplicate', { fileName: rawFile.name }));
      setAreAttachmentsUploading(false); // Reset uploading state if only this file was being processed
      return;
    }

    try {
      const newFile = await handleFileUpload(rawFile, user);
      setAttachmentFiles(prevFiles => [...prevFiles, newFile]);
      form.setFieldValue('material_paths', [...(form.getFieldValue('material_paths') || []), newFile]);
      message.success(t('aiAssistant.meetingSummary.form.messages.attachmentUploadSuccess', { fileName: newFile.source_name }));
    } catch (error) {
      console.error('附件上傳失敗:', error);
      message.error(t('aiAssistant.meetingSummary.form.messages.attachmentUploadFailed'));
    } finally {
      setAreAttachmentsUploading(false);
    }
  };

  const handleDeleteAttachmentFile = (fileToDelete) => {
    setAttachmentFiles(prevFiles => 
      prevFiles.filter(f => f.uid !== fileToDelete.uid)
    );
    form.setFieldValue(
      'material_paths', 
      (form.getFieldValue('material_paths') || []).filter(f => f.uid !== fileToDelete.uid)
    );
    // Also remove from permission issues if it existed there
    setFilePermissionIssues(prev => prev.filter(f => f.id !== fileToDelete.id));
    message.info(t('aiAssistant.meetingSummary.form.messages.attachmentRemoved', { fileName: fileToDelete.source_name || fileToDelete.name }));
  };

  const handleMultipleDriveAttachments = async (pickedFiles) => { // pickedFiles are from Google Picker
    setAreAttachmentsUploading(true);
    try {
      const currentAttachmentCount = attachmentFiles.length;
      const remainingSlots = MAX_ATTACHMENT_FILES - currentAttachmentCount;

      if (remainingSlots <= 0) {
        message.info(t('aiAssistant.meetingSummary.form.messages.attachmentLimitReached'));
        setAreAttachmentsUploading(false);
        return;
      }

      let filesToProcess = pickedFiles;
      if (pickedFiles.length > remainingSlots) {
        message.warning(t('aiAssistant.meetingSummary.form.messages.attachmentLimitExceeded', { 
          selected: pickedFiles.length, 
          remaining: remainingSlots 
        }));
        filesToProcess = pickedFiles.slice(0, remainingSlots);
      }

      if (filesToProcess.length === 0) {
        setAreAttachmentsUploading(false);
        return;
      }

      const fileIds = filesToProcess.map(f => f.id);
      const fileDetailsArray = await getDriveFiles(fileIds, false); 

      const newProcessedFiles = [];
      let skippedCount = 0;
      
      for (const fileDetail of fileDetailsArray) {
        // 從 pickedFiles 找到匹配的原始檔案資訊
        let originalFile = null;
        if (fileDetail && (fileDetail.status === 2 || fileDetail.file_info)) {
          const fileId = fileDetail.status === 2 
            ? fileDetail.source_info?.file_id 
            : fileDetail.file_info?.id;
          
          if (fileId) {
            originalFile = filesToProcess.find(f => f.id === fileId);
          }
        }
        
        const processedFile = processDriveFileResult(
          fileDetail, 
          originalFile || { id: fileDetail.requested_id }, 
          setFilePermissionIssues, 
          setHasAnyFilePermissionIssue, 
          setShowPermissionAlert,
          t
        );
        
        if (processedFile) {
          // 檢查是否已經存在於附件列表中
          const isDriveDuplicate = attachmentFiles.some(f => 
            f.source_source === 'drive' && 
            f.source_id === processedFile.id
          );
          
          if (isDriveDuplicate) {
            message.info(t('aiAssistant.meetingSummary.form.messages.driveFileExists', { fileName: processedFile.name }));
            skippedCount++;
            continue;
          }
          
          newProcessedFiles.push(processedFile);
          
          // 如果檔案處理成功但權限不足，顯示警告
          if (!processedFile.hasPermission) {
            message.warning(t('aiAssistant.meetingSummary.form.messages.driveFilePermissionWarning', { fileName: processedFile.name }));
          }
        } else {
          skippedCount++;
        }
      }
      
      setAttachmentFiles(prevFiles => [...prevFiles, ...newProcessedFiles]);
      form.setFieldValue('material_paths', [...(form.getFieldValue('material_paths') || []), ...newProcessedFiles]);
      if (newProcessedFiles.length > 0) {
        message.success(t('aiAssistant.meetingSummary.form.messages.driveAttachmentsAdded', { count: newProcessedFiles.length }));
      } else if (skippedCount > 0 && newProcessedFiles.length === 0) { 
        message.info(t('aiAssistant.meetingSummary.form.messages.driveFilesAllExist'));
      }

    } catch (error) {
      console.error('處理多個 Google Drive 附件失敗:', error);
      message.error(t('aiAssistant.meetingSummary.form.messages.driveMultipleAttachmentsFailed', { error: error.message || '未知錯誤' }));
    } finally {
      setAreAttachmentsUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!audioFile || !values.media_path) {
        message.error(t('aiAssistant.meetingSummary.form.messages.needAudioFile'));
        return;
      }

      if (isAudioUploading || areAttachmentsUploading) {
        message.warning(t('aiAssistant.meetingSummary.form.messages.waitForUpload'));
        return;
      }

      const formattedValues = {
        media_path: audioFile.source_url,
        material_path: attachmentFiles.map(f => f.source_url),
        meet_event_id: values.meet_event_id, // Will be undefined if manual mode
        additional_context: values.additional_context,
        target_language: values.target_language,
        required_topics: values.required_topics,
        output_format: values.output_format
      };
      
      const model = {
        model_provider: 'openai',
        model_level: 'advanced'
      }

      const result = await meetingSummarization({data: formattedValues, model: model});
      
      if (result.status === 200) {
        message.success(t('aiAssistant.meetingSummary.form.messages.submitSuccess'));
        onAfterSubmit(result); // Parent component handles this
        handleOverallCancel(); // Reset and close everything
      } else {
        message.error(result.message || t('aiAssistant.meetingSummary.form.messages.submitFailed'));
      }
    } catch (error) {
      if (error.errorFields) {
        message.error(t('aiAssistant.meetingSummary.form.messages.formIncomplete'));
      } else {
        console.error('提交表單時發生錯誤:', error);
        message.error(t('aiAssistant.meetingSummary.form.messages.processingError', { error: error.message || '未知錯誤' }));
      }
    }
  };

  const handleOpenCalendar = async () => {
    try {
      // 如果是從已有會議中重新選擇，不需要清除已有的檔案和設定
      // 因為 handleCalendarEventSelect 會在選擇新會議時處理

      if (!googleAccessTokenForCalendar) {
        const token = await getGoogleToken();
        if (!token) {
          message.error(t('aiAssistant.meetingSummary.form.messages.googleTokenFailed'));
          return;
        }
        setGoogleAccessTokenForCalendar(token);
      }
      setIsCalendarModalVisible(true);
    } catch (error) {
      message.error(t('aiAssistant.meetingSummary.form.messages.calendarOpenFailed', { error: error.message || '未知錯誤' }));
      console.error('Error opening calendar:', error);
    }
  };

  const handleCalendarEventSelect = async (event) => {
    if (event && event.id) {
      // 先關閉日曆模態框
      setIsCalendarModalVisible(false);
      setFormStep('formVisible');

      // 無論是否為相同會議，都先清除所有狀態
      setAudioFile(null);
      form.setFieldValue('media_path', null);
      setAttachmentFiles([]);
      form.setFieldValue('material_paths', []);
      setFilePermissionIssues([]);
      setHasAnyFilePermissionIssue(false);
      setShowPermissionAlert(true);

      // 設置會議詳情
      setSelectedEventDetails(event);
      form.setFieldsValue({ meet_event_id: event.id });
      message.success(t('aiAssistant.meetingSummary.form.messages.meetingSelected', { meetingTitle: event.title || '無標題會議' }));

      // 設置載入狀態 - 搜索週期性會議記錄和處理附件都需要顯示載入指示
      setAreAttachmentsUploading(true);

      // 搜索和載入週期性會議記錄（在處理當前會議附件之前）
      let recurringMeetingNotes = [];
      let hideSearchingMessage = null;
      try {
        // 顯示正在搜索週期性會議記錄的訊息
        hideSearchingMessage = message.loading(t('aiAssistant.meetingSummary.form.messages.recurringMeetingNotesSearching'), 0);
        
        recurringMeetingNotes = await searchAndLoadRecurringMeetingNotes(
          event, 
          googleAccessTokenForCalendar, 
          setFilePermissionIssues, 
          setHasAnyFilePermissionIssue, 
          setShowPermissionAlert, 
          t
        );
        
        // 隱藏搜索訊息
        if (hideSearchingMessage) {
          hideSearchingMessage();
        }
      } catch (error) {
        console.error('載入週期性會議記錄時發生錯誤:', error);
        // 確保隱藏搜索訊息
        if (hideSearchingMessage) {
          hideSearchingMessage();
        }
        // 不中斷流程，繼續處理當前會議的附件
      }

      if (event.attachments && event.attachments.length > 0) {
        const allCalendarAttachmentIds = [];
        for (const attachment of event.attachments) {
          const fileId = extractGoogleDriveId(attachment.url);
          if (fileId) {
            allCalendarAttachmentIds.push(fileId);
          }
        }

        if (allCalendarAttachmentIds.length === 0 && recurringMeetingNotes.length === 0) {
          message.info(t('aiAssistant.meetingSummary.form.messages.calendarNoProcessableFiles'));
          setAreAttachmentsUploading(false); // 清除載入狀態
          return; // No need to proceed with loading indicators
        }

        // 如果有音訊檔案需要處理，也設置音訊載入狀態
        if (allCalendarAttachmentIds.length > 0) {
          setIsAudioUploading(true);
        }

        try {
          let newAudioFileFromCalendar = null; 
          let newDocumentsFromCalendar = [...recurringMeetingNotes]; // 先加入會議記錄
          let audioFilesInCalendarCount = 0;
          let documentsSkippedLimitCount = 0;

          if (allCalendarAttachmentIds.length > 0) {
            const fileDetailsArray = await getDriveFiles(allCalendarAttachmentIds, false);

            for (const fileDetail of fileDetailsArray) {
              // 針對日曆事件，我們需要找到對應的原始附件資訊
              let baseInfo = null;
              if (fileDetail && (fileDetail.status === 2 || fileDetail.file_info)) {
                const fileId = fileDetail.status === 2 
                  ? fileDetail.source_info?.file_id 
                  : fileDetail.file_info?.id;
                
                if (fileId) {
                  // 找到對應的附件資訊
                  const attachmentInfo = event.attachments.find(a => {
                    const attachmentFileId = extractGoogleDriveId(a.url);
                    return attachmentFileId === fileId;
                  });
                  
                  if (attachmentInfo) {
                    baseInfo = {
                      id: fileId,
                      name: attachmentInfo.title,
                      source_name: attachmentInfo.title,
                      source_type: attachmentInfo.mimeType,
                      source_url: attachmentInfo.url,
                      url: attachmentInfo.url
                    };
                  }
                }
              }
              
              const processedFile = processDriveFileResult(
                fileDetail, 
                baseInfo || { id: fileDetail.requested_id }, 
                setFilePermissionIssues, 
                setHasAnyFilePermissionIssue, 
                setShowPermissionAlert,
                t
              );
              
              if (processedFile) {
                // 檢查是否為音訊/影片檔案
                if (isAudioVideoFile(processedFile.name, processedFile.source_type)) {
                  audioFilesInCalendarCount++;
                  // 記錄第一個找到的音訊檔案
                  if (!newAudioFileFromCalendar) {
                    newAudioFileFromCalendar = processedFile;
                  }
                } 
                // 檢查是否為文件檔案
                else if (isDocumentFile(processedFile.name, processedFile.source_type)) {
                  // 不再檢查是否已存在於現有附件列表，因為在選擇會議時已經清空了所有附件
                  // 只檢查是否在當前批次處理的檔案中已存在
                  const isDuplicateInCurrentBatch = newDocumentsFromCalendar.some(
                    f => f.source_id === processedFile.id
                  );
                  
                  if (isDuplicateInCurrentBatch) {
                    continue;
                  }
                  
                  // 檢查是否超過附件數量上限
                  if (attachmentFiles.length + newDocumentsFromCalendar.length < MAX_ATTACHMENT_FILES) {
                    newDocumentsFromCalendar.push(processedFile);
                  } else {
                    documentsSkippedLimitCount++;
                  }
                }
              }
            }
          }

          // Process the first found audio file from calendar
          if (newAudioFileFromCalendar) {
            if (audioFile && audioFile.id !== newAudioFileFromCalendar.id) {
              message.warning(t('aiAssistant.meetingSummary.form.messages.calendarAudioReplaced', { 
                newFileName: newAudioFileFromCalendar.source_name, 
                oldFileName: audioFile.source_name 
              }));
            } else if (!audioFile) {
              message.success(t('aiAssistant.meetingSummary.form.messages.calendarAudioLoaded', { fileName: newAudioFileFromCalendar.source_name }));
            }
            setAudioFile(newAudioFileFromCalendar);
            form.setFieldValue('media_path', newAudioFileFromCalendar);
            
            if (audioFilesInCalendarCount > 1) {
              message.info(t('aiAssistant.meetingSummary.form.messages.calendarMultipleAudio', { 
                count: audioFilesInCalendarCount, 
                fileName: newAudioFileFromCalendar.source_name,
                remaining: audioFilesInCalendarCount - 1 
              }));
            }
          } else if (audioFilesInCalendarCount > 0 && audioFile) {
            message.info(t('aiAssistant.meetingSummary.form.messages.calendarMultipleAudioNoSelection', { 
              count: audioFilesInCalendarCount, 
              fileName: audioFile.source_name 
            }));
          } else if (audioFilesInCalendarCount === 0 && !audioFile){
            // No audio in calendar and no audio previously. No specific message needed for audio.
          }

          // Process document files from calendar (including recurring meeting notes)
          if (newDocumentsFromCalendar.length > 0) {
            setAttachmentFiles(prevFiles => {
              // If it was a re-selection, prevFiles would have been cleared already.
              // Otherwise, this correctly appends to existing manually added files or an empty list.
              return [...prevFiles, ...newDocumentsFromCalendar];
            });
            form.setFieldValue('material_paths', 
              [...(form.getFieldValue('material_paths') || []).filter(f => f.source_source !== 'drive' || !allCalendarAttachmentIds.includes(f.source_id)), ...newDocumentsFromCalendar]
            ); // More careful update for form field if mixing sources
            
            const currentAttachmentCount = newDocumentsFromCalendar.length - recurringMeetingNotes.length;
            if (currentAttachmentCount > 0) {
              message.success(t('aiAssistant.meetingSummary.form.messages.calendarDocumentsLoaded', { count: currentAttachmentCount }));
            }
          }

          // 僅在超出附件數量上限時顯示警告
          if (documentsSkippedLimitCount > 0) {
            message.warning(t('aiAssistant.meetingSummary.form.messages.calendarAttachmentLimitExceeded', { count: documentsSkippedLimitCount }));
          }
          
          if (newDocumentsFromCalendar.length === recurringMeetingNotes.length && !newAudioFileFromCalendar && audioFilesInCalendarCount === 0 && allCalendarAttachmentIds.length > 0 && documentsSkippedLimitCount === 0) {
             message.info(t('aiAssistant.meetingSummary.form.messages.calendarNoValidFiles'));
          }

        } catch (error) {
          message.error(t('aiAssistant.meetingSummary.form.messages.calendarAttachmentProcessFailed', { error: error.message || '未知錯誤' }));
          console.error("Error processing calendar attachments:", error);
        } finally {
          setIsAudioUploading(false);
          setAreAttachmentsUploading(false);
        }
      } else if (recurringMeetingNotes.length > 0) {
        // 只有週期性會議記錄，沒有當前會議的附件
        setAttachmentFiles(prevFiles => [...prevFiles, ...recurringMeetingNotes]);
        form.setFieldValue('material_paths', [...(form.getFieldValue('material_paths') || []), ...recurringMeetingNotes]);
        // 清除載入狀態
        setAreAttachmentsUploading(false);
      } else {
        // 沒有附件也沒有週期性會議記錄，清除載入狀態
        setAreAttachmentsUploading(false);
      }
    } else {
      // Event selection was cleared or invalid (e.g., user cancelled calendar without selection)
      setSelectedEventDetails(null);
      form.setFieldsValue({ meet_event_id: undefined });
      // If calendar was cancelled without selection, ensure form step is appropriate
      setIsCalendarModalVisible(false); // Ensure it is closed
      // If they cancelled from initial choice flow, reset to initial choice
      if (meetingSource === 'calendar' && !selectedEventDetails && !event) { 
        setFormStep('initialChoice');
        setMeetingSource(null);
      } else {
        setFormStep('formVisible'); // Otherwise, stay/go to form
      }
    }
  };

  const handleClearSelectedEvent = () => {
    setSelectedEventDetails(null);
    form.setFieldsValue({ meet_event_id: undefined });
    // Optionally, you might want to clear files that were auto-loaded from this event
    // For now, keeping them and user can manually remove.
    message.info(t('aiAssistant.meetingSummary.form.messages.meetingCleared'));
  };

  const handleInitialChoice = (choice) => {
    // 先執行完整的狀態重置（除了 formStep 和 meetingSource）
    form.resetFields();
    setAudioFile(null);
    setAttachmentFiles([]);
    setSelectedEventDetails(null);
    setIsAudioUploading(false);
    setAreAttachmentsUploading(false);
    setFilePermissionIssues([]);
    setHasAnyFilePermissionIssue(false);
    setShowPermissionAlert(true);
    
    // 然後設置選擇的來源和下一步
    setMeetingSource(choice);
    if (choice === 'calendar') {
      handleOpenCalendar(); // This will set isCalendarModalVisible to true
      // formStep will transition to 'formVisible' via handleCalendarEventSelect or calendar cancel
    } else if (choice === 'manual') {
      setFormStep('formVisible');
    }
  };

  useEffect(() => {
    // The hasAnyFilePermissionIssue state is primarily determined by whether the filePermissionIssues list is empty.
    setHasAnyFilePermissionIssue(filePermissionIssues.length > 0);
  }, [filePermissionIssues]); // Only dependent on filePermissionIssues

  if (!visible) { // If parent hides, nothing is rendered.
    return null;
  }

  return (
    <>
      {/* Initial Choice Modal - Staging 環境特殊處理: 隱藏選擇會議模態框 */}
      {!IS_STAGING && (
        <Modal
          title={t('aiAssistant.meetingSummary.form.initialChoice.title')}
          open={formStep === 'initialChoice'} // Controlled by formStep
          onCancel={handleOverallCancel} // Full reset and calls parent onCancel
          footer={null} // Custom footer with Card buttons
          centered
          width={500}
          className={`initial-choice-modal ${dark ? 'dark' : ''}`}
        >
        <div style={{ display: 'flex', justifyContent: 'space-around', padding: '20px 0' }}>
          <Card 
            hoverable 
            style={{ width: 220, textAlign: 'center' }} 
            onClick={() => handleInitialChoice('calendar')}
          >
            <div style={{ fontSize: '2em', marginBottom: '10px' }}>🗓️</div>
            <h3>{t('aiAssistant.meetingSummary.form.initialChoice.calendar.title')}</h3>
            <p>{t('aiAssistant.meetingSummary.form.initialChoice.calendar.description')}</p>
          </Card>
          <Card 
            hoverable 
            style={{ width: 220, textAlign: 'center' }}
            onClick={() => handleInitialChoice('manual')}
          >
            <div style={{ fontSize: '2em', marginBottom: '10px' }}>✍️</div>
            <h3>{t('aiAssistant.meetingSummary.form.initialChoice.manual.title')}</h3>
            <p>{t('aiAssistant.meetingSummary.form.initialChoice.manual.description')}</p>
          </Card>
        </div>
        </Modal>
      )}

      {/* Main Meeting Summary Form Modal */}
      <Modal
        className={`meeting-summary-modal ${dark ? 'dark' : ''}`}
        title={t('aiAssistant.meetingSummary.form.title')}
        open={formStep === 'formVisible'} // Controlled by formStep
        maskClosable={false}
        onCancel={handleOverallCancel}
        footer={[
          <Button key="cancel" onClick={handleOverallCancel}>
            {t('aiAssistant.meetingSummary.form.buttons.cancel')}
          </Button>,
          <Button disabled={isAudioUploading || areAttachmentsUploading || hasAnyFilePermissionIssue} key="submit" type="primary" onClick={handleSubmit}>
            {t('aiAssistant.meetingSummary.form.buttons.submit')}
          </Button>
        ]}
        centered
        width={800}
        destroyOnClose // Ensures form fields are reset if not handled by resetAllStates always
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ // Set initial values here for fields that depend on state but should be part of form
            target_language: 'zh-TW',
            // media_path: audioFile, // valuePropName handles this for SingleUpload
            // material_paths: attachmentFiles, // Managed by MultiUpload
          }}
        >
          {/* 要分析的影音檔 */}
          <Form.Item
            label={t('aiAssistant.meetingSummary.form.fields.audioFile.label')}
            name="media_path"
            rules={[{ required: true, message: t('aiAssistant.meetingSummary.form.fields.audioFile.required') }]}
            // valuePropName="file" // SingleUpload handles its own value and display
          >
            <SingleUpload
              file={audioFile}
              isUploading={isAudioUploading}
              onFileChange={handleAudioFileChange} // For local uploads
              onGoogleDriveClick={() => createPicker('audio')}
              onDeleteFile={() => {
                const removedFileId = audioFile?.id;
                setAudioFile(null);
                form.setFieldValue('media_path', null); // Clear form value as well
                if (removedFileId) {
                  setFilePermissionIssues(prev => prev.filter(f => f.id !== removedFileId));
                }
              }}
              acceptTypes={getAudioVideoAcceptTypes()}
              disabled={isAudioUploading}
            />
          </Form.Item>

          {/* 會議附件 */}
          <Form.Item
            label={t('aiAssistant.meetingSummary.form.fields.attachments.label')}
            name="material_paths"
          >
            <MultiUpload
              files={attachmentFiles}
              isUploading={areAttachmentsUploading}
              onFileAdd={handleAddAttachmentFile} // For local uploads
              onGoogleDriveClick={() => createPicker('attachment')}
              onFileDelete={handleDeleteAttachmentFile}
              acceptTypes={getDocumentAcceptTypes()}
              maxFiles={MAX_ATTACHMENT_FILES}
            />
          </Form.Item>

          {hasAnyFilePermissionIssue && showPermissionAlert && (
            <Alert
              message={t('aiAssistant.meetingSummary.form.permissions.warning.title')}
              description={
                <div>
                  <p>{t('aiAssistant.meetingSummary.form.permissions.warning.description')}</p>
                  {filePermissionIssues.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <p>{t('aiAssistant.meetingSummary.form.permissions.warning.filesWithIssues')}</p>
                      <div>
                        {filePermissionIssues.map(file => (
                          <Tag 
                            color="error" 
                            key={file.id} 
                            style={{ margin: '2px', cursor: file.url ? 'pointer' : 'default' }}
                            onClick={() => file.url && window.open(file.url, '_blank')}
                          >
                            {file.url ? (
                              <span style={{ textDecoration: 'underline' }}>{file.name}</span>
                            ) : (
                              file.name
                            )}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              }
              type="warning"
              showIcon
              closable
              onClose={() => setShowPermissionAlert(false)}
              style={{ marginBottom: '16px' }}
            />
          )}

          <Row gutter={16}>
            <Col span={meetingSource === 'manual' ? 24 : 14}>
              <Form.Item
                label={t('aiAssistant.meetingSummary.form.fields.additionalContext.label')}
                name="additional_context"
              >
                <TextArea
                  placeholder={t('aiAssistant.meetingSummary.form.fields.additionalContext.placeholder')}
                  rows={(meetingSource === 'calendar' && selectedEventDetails) ? 6 : 4}
                />
              </Form.Item>
            </Col>
            {meetingSource !== 'manual' && (
              <Col span={10}>
                <Form.Item
                  label={t('aiAssistant.meetingSummary.form.fields.relatedMeeting.label')} // Label changed slightly
                  name="meet_event_id"
                  style={{ height: '100%' }}
                >
                  {selectedEventDetails ? (
                    <div className="selected-event-details" style={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                      <EventDetailsDisplay eventResource={selectedEventDetails} displayMode="minimal" />
                      <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                        <Button onClick={handleClearSelectedEvent} danger size="small">
                          {t('aiAssistant.meetingSummary.form.buttons.clearSelection')}
                        </Button>
                        <Button onClick={handleOpenCalendar} type="default" size="small">
                          {t('aiAssistant.meetingSummary.form.buttons.reselectMeeting')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // This state (no selected event but in calendar flow) should ideally be after clearing or if initial selection failed.
                    // The main way to get here is via '重新選擇' then cancelling calendar, or `handleClearSelectedEvent`
                    <Button onClick={handleOpenCalendar} style={{width: '100%'}}>
                      {t('aiAssistant.meetingSummary.form.buttons.selectMeeting')}
                    </Button>
                  )}
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* 目標語言 */}
          <Form.Item
            label={t('aiAssistant.meetingSummary.form.fields.targetLanguage.label')}
            name="target_language"
            // initialValue="zh-TW" // Moved to Form initialValues
          >
            <LanguageSelector
              value={selectedLanguage} // Keep this if LanguageSelector expects direct value prop
              onChange={value => {
                setSelectedLanguage(value);
                form.setFieldValue('target_language', value); // Ensure form value is also set
              }}
            />
          </Form.Item>

          <Divider />

          {/* 希望包含的總結項目 */}
          <Form.Item
            label={t('aiAssistant.meetingSummary.form.fields.requiredTopics.label')}
            name="required_topics"
          >
            <Select
              mode="tags"
              placeholder={t('aiAssistant.meetingSummary.form.fields.requiredTopics.placeholder')}
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Divider />

          <div style={{ display: 'flex', gap: '16px' }}>
            {/* 輸出格式 */}
            <Form.Item
              label={t('aiAssistant.meetingSummary.form.fields.outputFormat.label')}
              name="output_format"
              style={{ width: '150px' }}
              initialValue="docx" // Default for select
            >
              <Select placeholder={t('aiAssistant.meetingSummary.form.fields.outputFormat.label')}>
                <Select.Option value="md">{t('aiAssistant.meetingSummary.form.outputFormats.markdown')}</Select.Option>
                <Select.Option value="docx">{t('aiAssistant.meetingSummary.form.outputFormats.docx')}</Select.Option>
                <Select.Option value="txt">{t('aiAssistant.meetingSummary.form.outputFormats.txt')}</Select.Option>
              </Select>
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Calendar Modal */}
      <Modal
        title={t('aiAssistant.meetingSummary.form.calendar.title')}
        open={isCalendarModalVisible}
        onCancel={() => {
          // setIsCalendarModalVisible(false); // Handled by handleCalendarEventSelect or below direct set
          // If user cancels calendar, and they came from 'initialChoice' (meetingSource is 'calendar' but no event picked yet)
          if (meetingSource === 'calendar' && !selectedEventDetails) { 
             setIsCalendarModalVisible(false);
             setFormStep('initialChoice');
             setMeetingSource(null); // Reset meeting source as they aborted calendar selection
          } else if (meetingSource === 'calendar' && selectedEventDetails) {
            // If they had an event and reopened calendar then cancelled, stay on form with old event
            setIsCalendarModalVisible(false);
            setFormStep('formVisible');
          } else {
            // Fallback for other scenarios, e.g. manual flow opening calendar for picker? (less likely)
            setIsCalendarModalVisible(false);
          }
          // No specific token cleanup here, handleOpenCalendar will fetch if needed again.
        }}
        footer={null}
        width="80vw"
        centered
        destroyOnClose // Good for calendar to re-fetch events if needed
      >
        <MeetingCalendar 
          onSelectEvent={handleCalendarEventSelect}
          googleToken={googleAccessTokenForCalendar} // Pass the token
        />
      </Modal>
    </>
  );
};

export default MeetingSummaryForm;
